import { useQuery } from "@apollo/client";
import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import ExportActiveReport from "../../../components/Modals/ExportActiveReport";
import { GenerateReportBody } from "../../../components/Modals/GenerateReport";
import {
  Breadcrumb,
  Button,
  MenuButton,
  Modal,
  ResponsivePrimaryNav,
  SearchResultNavigation,
} from "../../../components/ui";
import {
  FETCH_EXHIBITION,
  FETCH_EXHIBITIONS_LIST,
} from "../../../graphql/query/Exhibition";
import { FETCH_LIST as fetchList } from "../../../graphql/query/List";
import useUser from "../../../hooks/useUser";
import { useSearchSynopsis } from "../../../hooks/useSearchSynopsis";
import { clearSelectionState } from "../../../util/clearSelectionState";
import {
  getSearchAwareLabel,
  markModuleSearchSubmitted,
  useModuleSearchSubmitted,
} from "../../../util/usePersistedFilterFormik";
import { scopeBulkActionTargets } from "../../../util/scopeBulkActionTargets";
import NewJob from "../../Jobs/components/NewJob";
import AddShipment from "../../Shipment/components/AddShipment";
import routes from "../pages/Wizard/routes";
import AdhocReportBody from "./Actions/AdhocReportBody";
import ConsignWork from "./Actions/ConsignWork";
import CopyDescription from "./Actions/CopyDescription";
import DuplicateExhibition from "./Actions/DuplicateExhibition";
import exportModule from "./Actions/ExportModule";
import NewExhibition from "./Actions/NewExhibition";
import ExhibitionSearch from "./ExhibitionSearch";
import {
  exhibitionFilterStore,
  exhibitionNavigationStore,
} from "./useExhibitionFilters";
import useSearchResultNavigation from "../../../hooks/useSearchResultNavigation";
import {
  buildExhibitionListUrl,
  buildExhibitionNavigationTargetUrl,
  getExhibitionListContext,
} from "./searchResultNavigation";

const PrimaryNav = props => {
  const { formik } = props;
  const location = useLocation();
  const isSearchSubmitted = useModuleSearchSubmitted(location.pathname);
  const navigate = useNavigate();
  const { hasPermission } = useUser();
  const search = exhibitionFilterStore.get();
  const pathnames = location?.pathname.split("/");
  const id = pathnames?.length > 2 ? pathnames[2] : null;
  const exhibitionId = id ? Number(id) : null;

  const exhibitionObj = {
    contact: {},
    exhibitionWorks: false,
  };
  const { data: { exhibition } = {} } = useQuery(
    FETCH_EXHIBITION(exhibitionObj),
    {
      skip: !id,
      variables: { id: +id },
    },
  );
  const path = pathnames[pathnames?.length - 1];
  const route = routes?.find(item => item?.href?.includes(path));
  const queryParams = new URLSearchParams(window.location.search);
  const status = queryParams.get("status") || "all";
  const listIdParam = queryParams.get("listId");
  const imageListIdParam = queryParams.get("imageListId");
  const listId = listIdParam ? +listIdParam : null;
  const imageListId = imageListIdParam ? +imageListIdParam : null;
  const listContext = useMemo(
    () => getExhibitionListContext(location.search),
    [location.search],
  );
  const selected = Object.keys(formik?.values?.selected?.ids || {});
  const ids = selected?.map(key => {
    const obj = formik?.values?.selected?.ids?.[key];
    return obj?.item?.id || obj?.id;
  });
  const select = formik?.values?.selected?.select;
  const {
    hasNavigationContext,
    hasNext,
    hasPrevious,
    navigationLoading,
    navigateToNext,
    navigateToPrevious,
    showNavigationControls,
  } = useSearchResultNavigation({
    activeSubPage: route?.href || "details",
    buildTargetUrl: buildExhibitionNavigationTargetUrl,
    dataKey: "exhibitionsV2",
    fetchQuery: FETCH_EXHIBITIONS_LIST,
    listContext,
    navigate,
    navigationStore: exhibitionNavigationStore,
    recordId: exhibitionId,
    status,
  });

  // Fetch list names when viewing saved lists (only if listId/imageListId is explicitly in URL and is a valid number)
  const { data: { list: savedList } = {} } = useQuery(fetchList(), {
    variables: { id: listId },
    skip: !listIdParam || !listId || isNaN(listId) || listId <= 0, // Only fetch if listId is explicitly in URL and valid
    errorPolicy: "ignore", // Ignore errors if list doesn't exist
  });

  const { data: { list: savedImageList } = {} } = useQuery(fetchList(), {
    variables: { id: imageListId },
    skip:
      !imageListIdParam ||
      !imageListId ||
      isNaN(imageListId) ||
      imageListId <= 0, // Only fetch if imageListId is explicitly in URL and valid
    errorPolicy: "ignore", // Ignore errors if list doesn't exist
  });

  const statusMap = {
    all: "All Exhibitions",
    searched: "Searched Exhibitions",
    selectListWorking:
      listIdParam && savedList?.name
        ? `Select List (${savedList.name})`
        : "Select List (Working)",
    imageSelectListWorking:
      imageListIdParam && savedImageList?.name
        ? `Image Select List (${savedImageList.name})`
        : "Image Select List (Working)",
  };
  const statusLabel = statusMap[status] || "All Exhibitions";

  const { synopsis: searchSynopsis } = useSearchSynopsis(
    exhibitionNavigationStore,
  );
  // With a submitted search the root links back to the search results;
  // the human-readable synopsis is shown as its tooltip.
  const rootLabel =
    isSearchSubmitted && status === "all"
      ? {
          label: getSearchAwareLabel(statusLabel, true),
          title: searchSynopsis
            ? `Exhibition Search ${searchSynopsis}`
            : undefined,
        }
      : statusLabel;
  const steps = [
    {
      label: rootLabel,
      onClick: () => navigate(buildExhibitionListUrl({ listContext, status })),
    },
    ...(exhibition
      ? [
          {
            label: exhibition?.title,
            onClick: () =>
              navigate(
                buildExhibitionNavigationTargetUrl({
                  listContext,
                  recordId: exhibition?.id,
                  status,
                }),
              ),
          },
          {
            label: route?.name,
            onClick: () =>
              navigate(
                buildExhibitionNavigationTargetUrl({
                  activeSubPage: route?.href,
                  listContext,
                  recordId: exhibition?.id,
                  status,
                }),
              ),
          },
        ]
      : []),
  ];

  const searchModalProps = {
    body: ExhibitionSearch,
    closeOnBackdrop: true,
    scale: "md",
    onSearchSubmitted: () => {
      clearSelectionState(formik);
      markModuleSearchSubmitted(location.pathname);
    },
  };

  // Detail pages share Formik with the list view, so bulk-action props must
  // be scoped to the URL id; otherwise list-side state leaks into actions
  // like Export here too. See scopeBulkActionTargets.
  const scopedTargets = scopeBulkActionTargets({
    entityId: id,
    selectedIds: ids,
    fallbacks: { listId, search, select },
  });

  const defaultActionProps = {
    closeOnBackdrop: false,
    hideCloseButton: true,
    ids: scopedTargets.ids,
    listId: scopedTargets.listId,
    onClose: () => clearSelectionState(formik),
    scale: "md",
    search: scopedTargets.search,
    select: scopedTargets.select,
  };

  // The exhibitions a brochure would be built from: the one being viewed on a
  // detail page, otherwise the explicit list selection.
  const brochureTargets = id ? [+id] : (ids || []).filter(Boolean);

  const [isExportReportOpen, setIsExportReportOpen] = useState(false);
  const actionMenuProps = {
    label: "Actions",
    options: [
      {
        label: "New Exhibition",
        disabled: !hasPermission("UPDATE_EXHIBITION"),
        modalProps: {
          body: NewExhibition,
          disabled: !hasPermission("UPDATE_EXHIBITION"),
          ...defaultActionProps,
        },
      },
      {
        label: "Export Exhibition Report",
        modalProps: {
          body: ExportActiveReport,
          obj: {
            ...exhibitionObj,
            contact: {
              contactCompany: {},
            },
            exhibitionWorks: {
              reportData: true,
            },
          },
          type: "Exhibition",
          ids: exhibition?.id ? [exhibition?.id] : ids,
          ...defaultActionProps,
          isExportReportOpen,
          onOpen: () => {
            setIsExportReportOpen(true);
          },
          onClose: () => {
            setIsExportReportOpen(false);
          },
        },
      },
      {
        disabled: !ids.length && !id,
        label: "Create an Adhoc Report",
        modalProps: {
          body: AdhocReportBody,
          ...defaultActionProps,
          exportModule,
        },
      },
      {
        // The brochure is built from instances, so the selected exhibitions are
        // sent as `exhibitionIds` and expand to every work hung in them. Only
        // explicit selections work: an exhibition search can't be translated
        // into the instance search the wizard resolves its cohort with.
        disabled: brochureTargets.length === 0,
        label: (
          <>
            PDF Generator{" "}
            <span
              className={`ml-1 inline-flex items-center rounded-full px-1.5 text-[8px] font-semibold leading-4 ${
                brochureTargets.length === 0
                  ? "bg-gray-100 text-gray-400"
                  : "bg-green-100 text-green-700"
              }`}
            >
              BETA
            </span>
          </>
        ),
        modalProps: {
          ...defaultActionProps,
          body: GenerateReportBody,
          type: "Instance",
          report: "brochure",
          ids: [],
          exhibitionIds: brochureTargets,
          listId: null,
          search: null,
          select: undefined,
          scale: "lg",
          onClose: () => {},
        },
      },
      {
        disabled: !ids.length && !id,
        label: (
          <>
            Reporting{" "}
            <span
              className={`ml-1 inline-flex items-center rounded-full px-1.5 text-[8px] font-semibold leading-4 ${
                !ids.length && !id
                  ? "bg-gray-100 text-gray-400"
                  : "bg-green-100 text-green-700"
              }`}
            >
              BETA
            </span>
          </>
        ),
        modalProps: {
          ...defaultActionProps,
          body: GenerateReportBody,
          type: "Exhibition",
          onClose: () => {},
        },
      },
      ...(id
        ? [
            {
              label: "Duplicate Exhibition",
              modalProps: {
                body: DuplicateExhibition,
                closeOnBackdrop: true,
                exhibition,
                hideCloseButton: true,
                scale: "sm",
              },
            },
            {
              label: "Copy Description",
              modalProps: {
                title: "Copy description?",
                description:
                  "Do you wish to copy the description from the exhibition?",
                scale: "sm",
                body: CopyDescription,
                closeOnBackdrop: true,
                exhibitionId: exhibition?.id,
                hideCloseButton: true,
              },
            },
            {
              label: "Consign Work",
              disabled: exhibition?.exhibitionWorks?.length < 1,
              modalProps: {
                scale: "md",
                body: ConsignWork,
                closeOnBackdrop: true,
                hideCloseButton: true,
                exhibition: exhibition,
              },
            },
            {
              label: "Loan Work",
              disabled: exhibition?.exhibitionWorks?.length < 1,
              modalProps: {
                scale: "md",
                body: ConsignWork,
                closeOnBackdrop: true,
                hideCloseButton: true,
                exhibition: exhibition,
                typeId: 2,
              },
            },
            {
              label: "Ship Work",
              disabled: exhibition?.exhibitionWorks?.length < 1,
              modalProps: {
                scale: "md",
                body: AddShipment,
                closeOnBackdrop: true,
                hideCloseButton: true,
                exhibition: exhibition,
              },
            },
            {
              disabled:
                !hasPermission("CREATE_JOB") ||
                exhibition?.exhibitionWorks?.length < 1,
              label: "Create Job",
              modalProps: {
                body: NewJob,
                scale: "md",
                exhibitionId: id,
                closeOnBackdrop: false,
                hideCloseButton: true,
              },
            },
          ]
        : []),
    ],
  };

  const breadcrumbProps = {
    currentStepIndex: steps?.length - 1,
    onChange: index => steps[index]?.onClick(),
    steps: steps?.map(item => item?.label),
  };

  return (
    <ResponsivePrimaryNav>
      <div>
        <Breadcrumb {...breadcrumbProps} />
      </div>
      <div className="flex items-center gap-2 sm:gap-4 lg:justify-end">
        <div id="form-save-status" />
        <SearchResultNavigation
          hasNavigationContext={hasNavigationContext}
          hasNext={hasNext}
          hasPrevious={hasPrevious}
          navigationLoading={navigationLoading}
          navigateToNext={navigateToNext}
          navigateToPrevious={navigateToPrevious}
          showNavigationControls={showNavigationControls}
        />
        <Modal {...searchModalProps}>
          <Button label="Exhibition Search" action="black" />
        </Modal>
        <MenuButton {...actionMenuProps} commandPalette />
      </div>
    </ResponsivePrimaryNav>
  );
};

export default PrimaryNav;
